"use client";

import { size, theme } from "@/config/theme";
import { SCContainerContent } from "@/utils/styledComponents";
import styled from "styled-components";
import Collapsible from "./Component-Collapsible";
import { useState } from "react";
import FAQTabItem from "./Component-FAQTabItem";
import { breakpoints } from "@/utils/breakpoint";
import { useTranslations } from "next-intl";
import { FilloutStandardEmbed } from "@fillout/react";
import "@fillout/react/style.css";
import { appSelectedCountry } from "@/recoil/appState";
import { useRecoilValue } from "recoil";
import {
    trackLeadFormSubmit,
    trackWhatsAppLinkClick
} from "@/utils/metaTracking";
import { trackGenerateLead } from "@/utils/ga4";
import { useParams, useRouter } from "next/navigation";

import FaqSchemaContactPage from "../../schema/FaqSchemaContactPage";
import { canonicalPath } from "@/utils/canonicalPath";

const Support = () => {
    const t = useTranslations("Support");
    const params = useParams();
    const router = useRouter();
    const locale = (params.locale as string) || "sg";
    const selectedCountry = useRecoilValue(appSelectedCountry);
    const [currentTab, setCurrentTab] = useState("generalInformation");
    const countryCode = selectedCountry === "id" ? "62" : "65";

    const handleTabClick = (tab: string) => {
        setCurrentTab(tab);
    };

    const tabList = [
        {
            id: "generalInformation",
            name: "General Information"
            // name: t("General Information")
        },
        {
            id: "enrollmentAndPayment",
            name: "Enrollment and Payment"
            // name: t("Enrollment and Payment")
        },
        {
            id: "classStructureAndProgression",
            name: "Class Structure and Progression"
            // name: t("Class Structure and Progression")
        },
        {
            id: "poolFacilities",
            name: "Pool Facilities"
            // name: t("Pool Facilities")
        }
    ];

    const faqListGeneralInformation = [
        {
            key: "1",
            label: "What is the best age to start swimming lessons for kids?",
            children:
                "The best age to start swimming lessons with us is 4 years old. At this age, children are more receptive to taking in instruction, which helps enhance their physical, cognitive, and emotional development through swimming."
        },
        {
            key: "2",
            label: "How long does it usually take for a child to learn to swim a short distance?",
            children:
                "On average, it takes about 40 lessons to swim a short distance. Progress varies based on individual learning pace and lesson consistency. "
        },
        {
            key: "3",
            label: "Do parents need to be in the water with their child during lessons?",
            children:
                "No, we recommend parents allow their child to spend sufficient time building a bond with their coach during trial sessions. This approach facilitates effective learning and trust-building between the child and the coach."
        },
        {
            key: "4",
            label: "What should I bring to my swimming lesson on the first day?",
            children:
                "Bring swimwear, goggles, a swim cap, and a kickboard. For children under 8, a thermal swimsuit is recommended to keep warm. If you do not have goggles, a swim cap, or a kickboard, you can purchase these items from the admin, and the coach will pass them to you on the trial date."
        },
        {
            key: "5",
            label: "How do I enter the swimming complex?",
            children:
                "Tickets can be purchased on the ActiveSG website. Once you have the ticket, scan the ticket barcode at the pool entrance to gain entry."
        },
        {
            key: "6",
            label: "Are there swimming lessons on the fifth week of every month?",
            children:
                "Yes, lessons are conducted on the fifth week of every month unless otherwise informed by the school."
        },
        {
            key: "7",
            label: "Will swimming lessons be conducted on rainy days?",
            children:
                "Yes, lessons proceed as usual. In cases of heavy rain with thunder or lightning, a bad weather program will be conducted, often involving land drills in sheltered areas."
        },
        {
            key: "8",
            label: "Are replacement classes available if students are unable to attend due to personal reasons or illness?",
            children:
                "Replacement classes are eligible for Gold Package only, and can be arranged if you inform the swim school at least one day in advance. For absences due to illness, a medical certificate is required. Replacement classes are provided in the form of Lesson Credits, which can be booked via the Parent Portal."
        },
        {
            key: "9",
            label: "Do I need to pay any registration fees or deposits?",
            children:
                "No registration fees or deposits are required. However, students learning at ActiveSG swimming pools must pay the entrance fees."
        },
        {
            key: "10",
            label: "What should I do if my child is unable to follow through the trial?",
            children:
                "Stay patient and positive. Try to understand the possible reasons for your child’s discomfort and address them calmly. Avoid leaving the pool immediately to prevent your child from associating crying with leaving the lesson."
        },
        {
            key: "11",
            label: "Can I have private swimming lessons at my condominium or apartment?",
            children:
                "Yes, private lessons can be conducted at your residence pool, subject to management approval for instructor entry."
        }
    ];

    const faqListEnrollmentAndPayment = [
        {
            key: "1",
            label: "Who do I contact for swimming lesson enquiries or make-up/replacement lessons?",
            children:
                "Please contact our Support Team via WhatsApp at 6582221357 for absence, lesson enrollment enquiries, and/or other matters. It&aposs advisable to save our Support Team&aposs number into your mobile phone for WhatsApp broadcasting purposes."
        },
        {
            key: "2",
            label: "When and how do I make payment for the monthly swim lessons?",
            children: (
                <div>
                    Fees are collected via{" "}
                    <a href="https://stripe.com/en-sg/payments" target="_blank">
                        Stripe
                    </a>{" "}
                    <p>
                        Payment is due two weeks before the start of the
                        student&apos;s new term with no additional admin fee.
                        Payments can be made via Bank Transfer or PayNow UEN to
                        the school&apos;s designated account, with an additional
                        $5 admin fee per transaction. It&apos;s important to
                        confirm the specific payment details with the swim
                        school.
                    </p>
                </div>
            )
            // children:
            //     "Fees are collected via Stripe Payment, two weeks before the start of the student’s new term with no additional admin fee. Payments can be made via Bank Transfer or PayNow UEN to the school&aposs designated account, with an additional $5 admin fee per transaction. It&aposs important to confirm the specific payment details with the swim school."
        },
        {
            key: "3",
            label: "How do I register for a swimming class?",
            children:
                "We recommend that parents register for a trial class to determine if the learning environment is comfortable for their child before proceeding with full registration. Once you decide to enroll, inform us, and our support team will assist you with the registration process accordingly."
        }
    ];
    const faqListClassStructureAndProgression = [
        {
            key: "1",
            label: "How many students are there in group swimming lessons?",
            children:
                "Class sizes can vary depending on the location and popularity of timeslots. The average size of our group classes is 5 students, with a maximum of 10 students per class, in compliance with ActiveSG policies."
        },
        {
            key: "2",
            label: "What are the SwimSafer requirements?",
            children:
                "The SwimSafer program, recognized by the Ministry of Education (MOE), has specific requirements for each stage. These requirements are designed to ensure the safety and skill progression of participants as they advance through the program."
        },
        {
            key: "3",
            label: "Will my child’s progress be affected if they do not take the SwimSafer test?",
            children:
                "The SwimSafer test is optional and will not affect your child’s progress. However, participating in the test provides a formal assessment of their swimming skills and readiness for the next level."
        }
    ];
    const faqListPoolFacilities = [
        {
            key: "1",
            label: "What facilities are available at the swimming complex?",
            children: (
                <div>
                    <p>
                        Facilities can vary between swimming complexes. Some may
                        offer sheltered pools and other amenities. It&apos;s
                        advisable to check for details about the available
                        facilities.
                    </p>
                    <a
                        href="https://www.activesgcircle.gov.sg/facilities/swimming-pools"
                        target="_blank"
                    >
                        here
                    </a>{" "}
                    .
                </div>
            )
        }
    ];

    return (
        <SCSupport>
            <FaqSchemaContactPage />
            <SCSupportContent>
                <div className="text-container">
                    <div className="text-largexlarge">
                        {t("Whatsapp Support")}
                    </div>
                    <div className="text-medium">
                        {selectedCountry === "sg" && (
                            <a
                                href="https://wa.me/+6582221357"
                                target="_blank"
                                onClick={() =>
                                    trackWhatsAppLinkClick("Support Page SG")
                                }
                            >
                                <b>+65 8222 1357</b>
                            </a>
                        )}
                        {selectedCountry === "id" && (
                            <a
                                href="https://wa.me/+628567872804"
                                target="_blank"
                                onClick={() =>
                                    trackWhatsAppLinkClick("Support Page ID")
                                }
                            >
                                <b>+62 856‑7872‑804</b>
                            </a>
                        )}
                    </div>
                    {selectedCountry === "sg" && (
                        <>
                            <div className="text-medium">{t("Mon - Sun")}</div>
                            <div className="text-medium">10:00am - 9:00pm</div>
                        </>
                    )}
                    {selectedCountry === "id" && (
                        <>
                            <div className="text-medium">Senin - Jumat</div>
                            <div className="text-medium">9:00am - 5:00pm</div>
                            <br />
                            <div className="text-medium">Sabtu & Minggu</div>
                            <div className="text-medium">8:00am - 4:00pm</div>
                        </>
                    )}
                </div>
                <div className="text-container">
                    <div className="text-largexlarge">
                        {t("Need More Assistance? Email Us via This Form")}
                    </div>
                    <div className="text-medium">
                        {t("We generally respond within 3 business days")}
                    </div>
                    <div style={{ width: "100%", height: "680px" }}>
                        <FilloutStandardEmbed
                            filloutId="xtcqPFBF12us"
                            parameters={{
                                phone: countryCode
                            }}
                            onSubmit={() => {
                                trackLeadFormSubmit("Contact Form");
                                // GA4 side of the same conversion (TD-163).
                                trackGenerateLead({
                                    form_name: "Contact Form"
                                });
                                // Delay navigation to ensure tracking completes
                                setTimeout(() => {
                                    router.push(
                                        canonicalPath(`/${locale}/thank-you`)
                                    );
                                }, 100);
                                // Prevent Fillout's default redirect
                                return false;
                            }}
                            inheritParameters
                        />
                    </div>
                    {/* <div className="text-largexlarge">{t("Email Us")}</div>
                    <div className="text-medium">
                        <b>support@theswimstarter.com</b>
                    </div>
                    <br />
                    <div className="text-medium">
                        {t("We generally respond within 3 business days")}
                    </div> */}
                </div>
                {/* <div className="form-container">Form Placeholder</div> */}
            </SCSupportContent>
            <br />
            <br />
            <SCSupportContent>
                <div className="text-container faq">
                    <div className="text-largexlarge">
                        {t("Frequently Asked Questions")}
                    </div>
                    <div className="tab-container">
                        {tabList.map((item) => (
                            <FAQTabItem
                                key={item.id}
                                item={item}
                                currentTab={currentTab}
                                handleClick={handleTabClick}
                            />
                        ))}
                    </div>
                </div>
                <div className="collapsible-container">
                    {currentTab === "generalInformation" && (
                        <Collapsible items={faqListGeneralInformation} />
                    )}
                    {currentTab === "enrollmentAndPayment" && (
                        <Collapsible items={faqListEnrollmentAndPayment} />
                    )}
                    {currentTab === "classStructureAndProgression" && (
                        <Collapsible
                            items={faqListClassStructureAndProgression}
                        />
                    )}
                    {currentTab === "poolFacilities" && (
                        <Collapsible items={faqListPoolFacilities} />
                    )}
                </div>
            </SCSupportContent>
        </SCSupport>
    );
};

export default Support;

const SCSupport = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: ${theme["spacing-lxl"]} 0;
`;

const SCSupportContent = styled(SCContainerContent)`
    display: flex;
    flex-direction: row;

    // Mobile view
    ${breakpoints(
        "flex-direction",
        "",
        [{ [size.px.sxs]: "column" }],
        "max-width"
    )};

    .text-container {
        flex: 1;

        // Mobile view
        ${breakpoints(
            "margin-bottom",
            "px",
            [{ [size.px.sxs]: "20" }],
            "max-width"
        )};

        &:last-child {
            // Mobile view
            ${breakpoints(
                "margin-bottom",
                "",
                [{ [size.px.sxs]: "0" }],
                "max-width"
            )};
        }

        &.faq {
            // Mobile view
            ${breakpoints(
                "margin-bottom",
                "",
                [{ [size.px.sxs]: "10px" }],
                "max-width"
            )};
        }

        .text-medium {
            font-size: ${theme["fontsize-m"]};
            color: ${theme["color-grey"]};
        }

        .text-largexlarge {
            font-size: ${theme["fontsize-lxl"]};
            font-weight: 500;
            margin-bottom: ${theme["spacing-sm"]};
        }

        .tab-container {
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;

            > div {
                margin: 0 ${theme["spacing-sm"]} ${theme["spacing-sm"]} 0;
            }
        }
    }

    .form-container {
        flex: 1;
    }

    .collapsible-container {
        flex: 2;
    }
`;

// const SCFAQContent = styled(SCContainerContent)`
//     display: flex;
//     flex-direction: column;
// `;
